import {
  cloneElement, isValidElement, useEffect, useId, useMemo, useRef, useState,
  type ReactElement, type ReactNode,
} from 'react';
import { useApp, useMoney, usePrivateMoney } from './store.tsx';
import { formatAxisTick, formatDate, signedAmount } from './format.ts';
import { api, type SafeToSpend, type Transaction } from './api.ts';

/* ============================================================================
   Shared UI. Charts are hand-built SVG rather than a charting library: the
   shapes needed here are simple, and it keeps the bundle small enough to load
   instantly on a phone.
   ========================================================================= */

// ── Primitives ───────────────────────────────────────────────────────────────

export function Card({ children, className = '', ...rest }: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={`card ${className}`} {...rest}>{children}</div>;
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }): JSX.Element {
  return (
    <div className="row row--between" style={{ marginBlock: '18px 8px' }}>
      <div className="section-title" style={{ margin: 0 }}>{children}</div>
      {action}
    </div>
  );
}

export function Empty({ icon, title, body, action }: { icon: string; title: string; body?: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="empty">
      <div className="empty__icon">{icon}</div>
      <div className="empty__title">{title}</div>
      {body && <div className="small">{body}</div>}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  );
}

export function Skeleton({ height = 60, count = 3 }: { height?: number; count?: number }): JSX.Element {
  return (
    <div className="stack">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton" style={{ height }} />
      ))}
    </div>
  );
}

/**
 * The launch screen.
 *
 * This is the first thing anyone sees, and on a Home Screen app it is shown
 * cold, on a blank canvas, with no browser chrome to explain what is happening.
 * A bare spinner on flat white reads as a page that failed to load — which is
 * an unfortunate first impression for something holding your money.
 *
 * Everything here is CSS on a handful of elements: a launch screen that costs
 * layout work delays the very thing it exists to cover.
 */
export function Splash({ label }: { label?: string }): JSX.Element {
  return (
    <div className="splash">
      <div className="splash__inner">
        <div className="splash__mark">
          <span className="splash__ring" aria-hidden />
          <span aria-hidden>◈</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div className="splash__name">Fintrack</div>
          {label && <div className="splash__tagline" style={{ marginTop: 4 }}>{label}</div>}
        </div>
        <div className="splash__bars" role="status" aria-label={label ?? 'Loading'}>
          <span className="splash__bar" />
          <span className="splash__bar" />
          <span className="splash__bar" />
        </div>
      </div>
    </div>
  );
}

export function Spinner({ label }: { label?: string }): JSX.Element {
  return (
    <div className="row" style={{ gap: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3" opacity="0.2" />
        <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite" />
        </path>
      </svg>
      {label}
    </div>
  );
}

/**
 * Provenance badge — the single most important trust affordance in the app.
 * Users must never have to guess whether a number is a fact or a projection.
 */
export function ProvenanceBadge({ kind }: { kind: 'confirmed' | 'inferred' | 'estimated' | 'ai' }): JSX.Element {
  const { t } = useApp();
  const label = kind === 'ai' ? t('aiSuggested')
    : kind === 'confirmed' ? t('confirmed')
    : kind === 'inferred' ? t('inferred')
    : t('estimated');
  const title = kind === 'ai' ? t('aiConsentHelp')
    : kind === 'confirmed' ? t('confirmedHelp')
    : kind === 'inferred' ? t('inferredHelp')
    : t('estimatedHelp');
  return <span className={`badge badge--${kind}`} title={title}>{label}</span>;
}

/** Categorization confidence, shown as a dot plus words — never colour alone. */
export function ConfidenceDot({ value }: { value: number | null }): JSX.Element | null {
  if (value == null) return null;
  const level = value >= 0.8 ? 'high' : value >= 0.55 ? 'med' : 'low';
  return (
    <span className="conf">
      <span className={`conf__dot conf__dot--${level}`} />
      {Math.round(value * 100)}%
    </span>
  );
}

/** How far the sheet has to be pulled down before letting go dismisses it. */
const SHEET_DISMISS_PX = 96;

export function Sheet({ title, onClose, children }: { title?: string; onClose: () => void; children: ReactNode }): JSX.Element {
  const { t } = useApp();

  // Escape closes, and the body must not scroll behind an open sheet.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  /**
   * Pull-to-dismiss.
   *
   * `settling` is the difference between following a finger and springing back:
   * during the drag the sheet must track the touch exactly, with no transition,
   * or it lags behind and feels broken; on release it has to animate home.
   */
  const [drag, setDrag] = useState<{ y: number; settling: boolean } | null>(null);
  const startY = useRef(0);
  // The gesture is tracked in refs and only mirrored into state for rendering.
  // Reading it back out of state would mean deciding whether to dismiss from
  // whatever the last render happened to hold, and a flick is over in a few
  // frames — exactly the case where that is not the same number.
  const dragging = useRef(false);
  const offset = useRef(0);

  const onTouchStart = (e: React.TouchEvent): void => {
    startY.current = e.touches[0]!.clientY;
    dragging.current = true;
    offset.current = 0;
    setDrag({ y: 0, settling: false });
  };
  const onTouchMove = (e: React.TouchEvent): void => {
    if (!dragging.current) return;
    const dy = e.touches[0]!.clientY - startY.current;
    // Upward drags are damped rather than ignored: a sheet that is completely
    // rigid in one direction reads as frozen, and the resistance is what says
    // "this is as far as it goes".
    offset.current = dy > 0 ? dy : dy / 5;
    setDrag({ y: offset.current, settling: false });
  };
  const onTouchEnd = (): void => {
    if (!dragging.current) return;
    dragging.current = false;
    if (offset.current > SHEET_DISMISS_PX) onClose();
    else { offset.current = 0; setDrag({ y: 0, settling: true }); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label={title}>
      <div
        className="sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: drag && drag.y !== 0 ? `translateY(${drag.y}px)` : undefined,
          transition: drag?.settling ? 'transform 0.2s ease' : 'none',
        }}
      >
        {/*
          Three ways out, where there used to be one.

          Tapping the backdrop was it — which means the way to dismiss a sheet
          was to hit the one part of the screen that is not the thing you are
          looking at, and on a tall phone that target is at the top, out of
          thumb reach. The handle was drawn as though it could be dragged and
          could not be.

          Sticky, so Done stays reachable however far the content scrolls; the
          sheet itself is the scroll container.
        */}
        <div className="sheet__grab" onTouchStart={onTouchStart} onTouchMove={onTouchMove}
             onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}>
          <div className="sheet__handle" />
          <div className="sheet__bar">
            {title ? <div className="sheet__title">{title}</div> : <span />}
            <button className="btn btn--ghost btn--sm sheet__done" onClick={onClose}
                    aria-label={t('close')}>
              {t('done')}
            </button>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * A labelled form control.
 *
 * The label is bound to the control with `htmlFor`/`id`, and the hint or error
 * is wired through `aria-describedby`. Without that binding a screen reader
 * announces every field in the app as an unlabelled edit box — the visual
 * label is not enough — and an invalid field gives no audible reason.
 */
export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string | null; children: ReactNode }): JSX.Element {
  const generatedId = useId();
  const child = isValidElement(children) ? (children as ReactElement<{ id?: string }>) : null;
  const controlId = child?.props.id ?? generatedId;
  const messageId = error ? `${controlId}-error` : hint ? `${controlId}-hint` : undefined;

  const control = child
    ? cloneElement(child as ReactElement<Record<string, unknown>>, {
        id: controlId,
        'aria-describedby': messageId,
        'aria-invalid': error ? true : undefined,
      })
    : children;

  return (
    <div className="field">
      <label className="field__label" htmlFor={controlId}>{label}</label>
      {control}
      {error ? <div className="field__error" id={messageId}>{error}</div>
        : hint ? <div className="field__hint" id={messageId}>{hint}</div> : null}
    </div>
  );
}

/**
 * `hint` is a node rather than a string so a caller can colour part of it —
 * a countdown that ends with your salary back on screen is not a muted aside.
 */
export function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: ReactNode }): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="row row--between"
      style={{ width: '100%', textAlign: 'start', padding: '10px 0', gap: 14 }}
    >
      <span className="grow">
        <span style={{ display: 'block', fontWeight: 545, fontSize: 14.5 }}>{label}</span>
        {hint && <span className="tiny muted" style={{ display: 'block', marginTop: 3, lineHeight: 1.5 }}>{hint}</span>}
      </span>
      <span
        aria-hidden
        style={{
          flexShrink: 0, width: 46, height: 27, borderRadius: 99,
          background: checked ? 'var(--accent)' : 'var(--border-strong)',
          position: 'relative', transition: 'background 0.18s',
        }}
      >
        <span style={{
          position: 'absolute', top: 3, insetInlineStart: checked ? 22 : 3,
          width: 21, height: 21, borderRadius: 99, background: '#fff',
          transition: 'inset-inline-start 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        }} />
      </span>
    </button>
  );
}

// ── Transaction row ──────────────────────────────────────────────────────────

export function TransactionRow({ tx, onClick }: { tx: Transaction; onClick?: () => void }): JSX.Element {
  const { locale, categoryMeta, categoryLabel } = useApp();
  const money = useMoney();
  const privateMoney = usePrivateMoney();
  const meta = categoryMeta(tx.category);
  const signed = signedAmount(tx.amountMinor, tx.transactionType);
  const isInflow = signed > 0;
  // The salary credit itself, which is the plainest statement of income the
  // app holds — covering the Income total elsewhere while this row spells it
  // out in the ledger would protect nothing. A refund is an inflow too and is
  // left alone: it says what a shop gave back, not what anyone earns.
  const amount = tx.transactionType === 'income' ? privateMoney : money;

  const subtitle = [
    categoryLabel(tx.category),
    tx.cardLast4 ? `•${tx.cardLast4}` : null,
    formatDate(tx.occurredAt, locale, { relative: true }),
  ].filter(Boolean).join(' · ');

  return (
    <button className="tx" onClick={onClick} type="button">
      <span className="tx__icon" style={{ background: `${meta.color}1f` }} aria-hidden>{meta.icon}</span>
      <span className="tx__body">
        <span className="tx__merchant">
          {tx.merchantDisplay || tx.merchantRaw || categoryLabel(tx.category)}
          {tx.status === 'needs_review' && <span className="badge badge--warning" style={{ marginInlineStart: 6 }}>!</span>}
        </span>
        <span className="tx__meta">{subtitle}</span>
      </span>
      <span className={`tx__amount ${isInflow ? 'money--pos' : ''}`}>
        {amount(Math.abs(signed), tx.currency)}
        {isInflow ? '' : ''}
      </span>
    </button>
  );
}

// ── Charts ───────────────────────────────────────────────────────────────────

export interface DonutSlice { key: string; value: number; color: string; label: string }

/**
 * Category donut. Slices below 3% are folded into one "other" wedge so the
 * ring stays legible instead of turning into a barcode.
 */
/**
 * Slices below this share are collapsed into a single grey remainder.
 *
 * Exported because the list beside the chart has to draw its swatches by the
 * same rule. A category shown with its own colour while the chart folded it
 * into the grey is a legend that points at nothing.
 */
export const DONUT_MIN_SHARE = 0.03;

export function DonutChart({ slices, centerValue, centerLabel, size = 168 }: {
  slices: DonutSlice[];
  centerValue: string;
  centerLabel: string;
  size?: number;
}): JSX.Element {
  const total = slices.reduce((s, x) => s + x.value, 0);
  const stroke = 22;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  const visible = useMemo(() => {
    if (!total) return [];
    const major = slices.filter((s) => s.value / total >= DONUT_MIN_SHARE);
    const minorTotal = total - major.reduce((s, x) => s + x.value, 0);
    return minorTotal > 0
      ? [...major, { key: '__other', value: minorTotal, color: 'var(--border-strong)', label: '—' }]
      : major;
  }, [slices, total]);

  if (!total) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="chart" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--bg-sunken)" strokeWidth={stroke} />
      </svg>
    );
  }

  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="chart" role="img" aria-label={`${centerLabel}: ${centerValue}`}>
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {visible.map((s) => {
          const fraction = s.value / total;
          const dash = fraction * circumference;
          const el = (
            <circle
              key={s.key}
              cx={size / 2} cy={size / 2} r={radius}
              fill="none" stroke={s.color} strokeWidth={stroke}
              strokeDasharray={`${Math.max(0, dash - 2)} ${circumference}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += dash;
          return el;
        })}
      </g>
      <text x={size / 2} y={size / 2 - 7} className="donut__center donut__value">{centerValue}</text>
      <text x={size / 2} y={size / 2 + 13} className="donut__center donut__label">{centerLabel}</text>
    </svg>
  );
}

/**
 * Cumulative spend for the period, against the same point in the previous one.
 *
 * Daily bars were the obvious choice here and they were the wrong one: a single
 * rent payment is 20× a typical day, so every other day flattened into nothing.
 * A running total is immune to that — one big charge becomes a step rather than
 * a spike — and it answers the question people actually have mid-month, which
 * is "am I ahead of where I was last month?".
 */
export function PaceChart({ points, previousPoints, currency, height = 104, currentLabel }: {
  points: { key: string; label: string; spentMinor: number }[];
  previousPoints?: { key: string; label: string; spentMinor: number }[];
  currency: string;
  height?: number;
  /** Names the period being charted. Defaults to "this month" for callers that
      are always monthly; passing it stops a weekly chart claiming to be one. */
  currentLabel?: string;
}): JSX.Element {
  const { locale, t } = useApp();
  const money = useMoney();

  const cumulative = (series: { spentMinor: number }[]): number[] => {
    let run = 0;
    return series.map((p) => (run += p.spentMinor));
  };

  const current = cumulative(points);
  const previous = previousPoints?.length ? cumulative(previousPoints) : [];

  // Only the elapsed part of the current period is meaningful; trailing zero
  // days are days that have not happened yet, not days of zero spending.
  // The cutoff must come from the *daily* series — the cumulative one is
  // monotonic, so every day after the first purchase looks non-zero.
  const lastActive = points.reduce((last, p, i) => (p.spentMinor > 0 ? i : last), 0);
  const activeCurrent = current.slice(0, lastActive + 1);

  const width = 100;
  // Rounded up to a round number so the gridlines can be labelled with figures
  // somebody would say out loud. Scaling to the exact peak instead gives an
  // axis reading 4,003 / 2,002 / 0, which is precise and unusable.
  const max = niceCeiling(Math.max(...current, ...previous, 1));
  const span = Math.max(points.length - 1, previous.length - 1, 1);

  // The plot sits 2px inside the box so a flat line at zero is not clipped by
  // the edge. The axis labels are positioned with the same function, or they
  // would drift from the gridlines they name.
  const yFor = (v: number): number => height - (v / max) * (height - 8) - 2;

  const toPath = (series: number[], close: boolean): string => {
    if (!series.length) return '';
    const pts = series.map((v, i) => `${(i / span) * width},${yFor(v)}`);
    const line = `M${pts.join(' L')}`;
    return close ? `${line} L${((series.length - 1) / span) * width},${yFor(0)} L0,${yFor(0)} Z` : line;
  };

  const total = activeCurrent[activeCurrent.length - 1] ?? 0;
  const previousAtSamePoint = previous[Math.min(lastActive, previous.length - 1)];

  const gridValues = [max, max / 2, 0];
  const startsAt = points[0]?.key;
  const endsAt = points[points.length - 1]?.key;

  return (
    <div className="pace-chart">
      <div className="pace">
        {/* A real axis, in HTML rather than inside the SVG: the chart is drawn
            with preserveAspectRatio="none" so that it stretches to the card
            width, and anything lettered inside it stretches with it. */}
        <div className="pace__axis-y" style={{ height }} aria-hidden>
          {gridValues.map((v) => (
            <span key={v} className="tiny pace__tick" style={{ top: yFor(v) }}>
              {formatAxisTick(v, currency, locale)}
            </span>
          ))}
        </div>

        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="chart pace__plot"
             style={{ height }} role="img"
             aria-label={`Cumulative spending ${money(total, currency)}`
                         + (startsAt && endsAt ? `, ${startsAt} to ${endsAt}` : '')}>
          {gridValues.map((v) => (
            <line key={v} className="chart__grid" x1="0" x2={width} y1={yFor(v)} y2={yFor(v)}
                  vectorEffect="non-scaling-stroke" />
          ))}
          {previous.length > 0 && (
            <path d={toPath(previous, false)} fill="none" stroke="var(--border-strong)"
                  strokeWidth="1.5" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
          )}
          <path d={toPath(activeCurrent, true)} fill="var(--accent)" opacity="0.12" />
          <path d={toPath(activeCurrent, false)} fill="none" stroke="var(--accent)"
                strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>

      {/* The dates the period actually runs between. On a payday cycle these are
          not the 1st and the 31st of anything, so leaving the axis bare left the
          chart covering a window the reader had to guess at. */}
      <div className="pace__axis-x tiny muted">
        <span>{axisDate(startsAt, locale)}</span>
        <span>{axisDate(endsAt, locale)}</span>
      </div>

      <div className="row" style={{ gap: 14, marginTop: 8, justifyContent: 'center' }}>
        <Legend color="var(--accent)" label={currentLabel ?? t('thisMonth')} />
        {previousAtSamePoint != null && previousAtSamePoint > 0 && (
          <span className="row tiny muted" style={{ gap: 5 }}>
            <span style={{ width: 12, height: 0, borderTop: '1.5px dashed var(--border-strong)' }} />
            {t('previousPeriod')} {money(previousAtSamePoint, currency, { compact: true })}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The next round number at or above `v` — 1, 2, 2.5 or 5 times a power of ten.
 *
 * An axis exists to be read off, which means its top has to be a number with a
 * shape: 5,000 rather than 4,003.10. The chart loses a little height to the
 * rounding and gains a scale somebody can actually use.
 */
function niceCeiling(v: number): number {
  if (v <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(v));
  const scaled = v / magnitude;
  const step = [1, 2, 2.5, 5, 10].find((s) => scaled <= s + 1e-9) ?? 10;
  return step * magnitude;
}

/** `YYYY-MM-DD` as a short day and month, read as UTC because that is how the
    server keys its buckets — parsing it locally can shift it a day. */
function axisDate(key: string | undefined, locale: 'en' | 'ar'): string {
  if (!key) return '';
  const d = new Date(`${key}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return key;
  return d.toLocaleDateString(locale === 'ar' ? 'ar' : 'en-GB',
    { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Month-over-month comparison, spending and income side by side. */
function Legend({ color, label, faded }: { color: string; label: string; faded?: boolean }): JSX.Element {
  return (
    <span className="row tiny muted" style={{ gap: 5 }}>
      <span style={{ width: 9, height: 9, borderRadius: 2, background: color, opacity: faded ? 0.45 : 1 }} />
      {label}
    </span>
  );
}

/** Horizontal category bar with amount and share. */
export function CategoryBar({ categoryKey, amountMinor, sharePct, currency, swatchColor, onClick }: {
  categoryKey: string;
  amountMinor: number;
  sharePct: number;
  currency: string;
  /** Overrides the category colour when the chart folded this into its
      remainder, so the swatch matches the arc the reader is looking at. */
  swatchColor?: string;
  onClick?: () => void;
}): JSX.Element {
  const { categoryMeta, categoryLabel } = useApp();
  const money = useMoney();
  const meta = categoryMeta(categoryKey);

  return (
    <button className="cat-row" onClick={onClick} type="button" style={{ width: '100%', textAlign: 'start' }}>
      {/* A swatch rather than the category's emoji: this list sits directly
          under the donut and its job is to say which arc is which. An emoji
          names the category, which the label beside it already does; only the
          colour connects the row to the chart.

          Decorative, so hidden from assistive tech — the category name carries
          the meaning, and colour alone never should. */}
      <span
        className="cat-row__swatch"
        style={{ background: swatchColor ?? meta.color }}
        aria-hidden
      />
      <span className="cat-row__body">
        <span className="cat-row__top">
          <span className="cat-row__name">{categoryLabel(categoryKey)}</span>
          <span className="cat-row__value">{money(amountMinor, currency, { compact: true })}</span>
        </span>
        <span className="bar">
          <span className="bar__fill" style={{ width: `${Math.min(100, sharePct)}%`, background: meta.color }} />
        </span>
      </span>
    </button>
  );
}

// ── Period picker ────────────────────────────────────────────────────────────

/**
 * The windows offered on screen.
 *
 * "Last 30 days" is gone: this month *is* roughly the last thirty days, since
 * the cycle runs payday to payday, so the two sat side by side answering the
 * same question with figures that disagreed by a few days' spending. What is
 * left is one window per question — this cycle, the one before it, a quarter,
 * half a year, the year.
 *
 * The key still resolves server-side. A phone running a cached build keeps
 * asking for it until the new one takes, and answering that with a 400 would
 * turn a removed chip into a broken screen.
 */
export const PERIODS = ['today', 'week', 'month', 'last_month', 'last_90_days', 'last_6_months', 'year'] as const;
export type PeriodKey = typeof PERIODS[number];

export function PeriodPicker({ value, onChange }: { value: PeriodKey; onChange: (p: PeriodKey) => void }): JSX.Element {
  const { t } = useApp();
  const labels: Record<PeriodKey, string> = {
    today: t('today'), week: t('thisWeek'), month: t('thisMonth'),
    last_month: t('lastMonth'), last_90_days: t('last90'),
    last_6_months: t('last6Months'), year: t('thisYear'),
  };

  return (
    <div className="scroll-x" role="tablist">
      {PERIODS.map((p) => (
        <button
          key={p}
          role="tab"
          aria-selected={value === p}
          className={`chip${value === p ? ' chip--active' : ''}`}
          onClick={() => onChange(p)}
        >
          {labels[p]}
        </button>
      ))}
    </div>
  );
}

// ── Misc ─────────────────────────────────────────────────────────────────────

export function CopyableSecret({ value, label, hint }: { value: string; label: string; hint?: string }): JSX.Element {
  const { t } = useApp();
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard API needs a secure context; select-to-copy is the fallback.
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <div className="stack stack--tight">
      <div className="field__label">{label}</div>
      <div className="row" style={{ gap: 8 }}>
        <code className="mono grow truncate" style={{
          padding: '10px 12px', background: 'var(--bg-sunken)',
          borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', userSelect: 'all',
        }}>{value}</code>
        <button className="btn btn--secondary btn--sm" onClick={copy}>
          {copied ? t('copied') : t('copy')}
        </button>
      </div>
      {hint && <div className="field__hint">{hint}</div>}
    </div>
  );
}

/**
 * "Was this your salary?"
 *
 * The cycle starts at the salary that actually arrived rather than the day of
 * the month it was due, which means one credit decides where the month begins
 * — and therefore what every figure on the screen above is measured against.
 * When the bank labelled the credit there is nothing to ask. When it did not,
 * the app has guessed from the size, and a wrong guess is expensive: the cycle
 * restarts, the salary that funded the spending already done is left behind in
 * the old one, and safe to spend reports the lot as overspend.
 *
 * So the guess is stated rather than presented as a result, next to the figure
 * it produced. Answering either way settles it permanently — this never
 * reappears for the same credit.
 *
 * The amount is covered like every other income figure; the credit is named
 * and dated instead, which is what makes it recognisable at a glance anyway.
 */
export function SalaryQuestion({ question, currency, onAnswered }: {
  question: NonNullable<SafeToSpend['salaryQuestion']>;
  currency: string;
  onAnswered: () => void;
}): JSX.Element {
  const { locale, toastError } = useApp();
  const privateMoney = usePrivateMoney();
  const ar = locale === 'ar';
  const [busy, setBusy] = useState(false);

  const answer = async (isSalary: boolean) => {
    setBusy(true);
    try {
      await api.patch(`/transactions/${question.transactionId}`, { isSalary });
      onAnswered();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const named = question.merchant || (ar ? 'مبلغ وارد' : 'a credit');

  return (
    <Card style={{ marginBottom: 12, borderColor: 'var(--warning)' }}>
      <div className="small strong">
        {ar ? 'هل هذا راتبك؟' : 'Was this your salary?'}
      </div>
      <p className="tiny muted" style={{ marginTop: 6, lineHeight: 1.6 }}>
        {ar
          ? `${privateMoney(question.amountMinor, currency)} من ${named} في ${formatDate(question.occurredAt, locale)}. اعتُبر راتبك، فبدأت دورة جديدة من ذلك اليوم. إن لم يكن راتبك، ستعود الدورة إلى ما كانت عليه.`
          : `${privateMoney(question.amountMinor, currency)} from ${named} on ${formatDate(question.occurredAt, locale)} was taken to be your salary, so a new cycle started that day. If it was not, the cycle goes back to where it was.`}
      </p>
      <div className="row" style={{ gap: 8, marginTop: 10 }}>
        <button className="btn btn--secondary grow btn--sm" disabled={busy}
                onClick={() => void answer(true)}>
          {ar ? 'نعم، راتبي' : 'Yes, my salary'}
        </button>
        <button className="btn btn--secondary grow btn--sm" disabled={busy}
                onClick={() => void answer(false)}>
          {ar ? 'لا، ليس راتبي' : 'No, it is not'}
        </button>
      </div>
    </Card>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }): JSX.Element {
  const { t } = useApp();
  return (
    <Empty
      icon="⚠️"
      title={t('somethingWrong')}
      body={message}
      action={onRetry && <button className="btn btn--secondary" onClick={onRetry}>{t('retry')}</button>}
    />
  );
}
